/**
 * Стартовый набор сторис для mock-хранилища — те же четыре истории, что были
 * зашиты в клиент (`storyContent.ts`), теперь в форме контракта и со слайдами.
 * Картинок у нас нет, поэтому слайд — SVG 9:16: фон-градиент и текст. Когда
 * появится API, набор уйдёт вместе с этой папкой.
 */
import type { StoryDto } from '../contract'

interface Palette {
  from: string
  to: string
  ink: string
}

const PALETTES: Record<string, Palette> = {
  mint: { from: '#d9f5e8', to: '#8fd9b6', ink: '#0b3d2a' },
  sky: { from: '#dcecff', to: '#8dbcf2', ink: '#0d2c52' },
  sand: { from: '#fbefd9', to: '#efc98a', ink: '#4a300a' },
  rose: { from: '#fde3e6', to: '#f2a3ad', ink: '#561522' },
  lav: { from: '#ebe5fb', to: '#b6a5ec', ink: '#2b1d5c' },
}

const escape = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

/** Переносы по словам: SVG сам строки не переносит. */
export function wrap(text: string, width: number): string[] {
  const lines: string[] = []
  let line = ''
  for (const word of text.split(' ')) {
    const next = line ? `${line} ${word}` : word
    if (next.length > width && line) {
      lines.push(line)
      line = word
    } else {
      line = next
    }
  }
  if (line) lines.push(line)
  return lines
}

/**
 * Отступ текста от края слайда. Телефоны уже 9:16, и слайд, растянутый на
 * экран (`object-fit: cover`, как в Instagram), теряет по бокам до ~12 %
 * ширины — текст держим в безопасной зоне по центру.
 */
export const SAFE_X = 170

/** Слайд 1080×1920: заголовок крупно, под ним текст. */
export function slideSvg(palette: keyof typeof PALETTES, title: string, body = ''): string {
  const p = PALETTES[palette] as Palette
  const head = wrap(title, 13)
    .map((l, i) => `<tspan x="${SAFE_X}" dy="${i === 0 ? 0 : 108}">${escape(l)}</tspan>`)
    .join('')
  const text = wrap(body, 26)
    .map((l, i) => `<tspan x="${SAFE_X}" dy="${i === 0 ? 0 : 64}">${escape(l)}</tspan>`)
    .join('')
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1080 1920">` +
    `<defs><linearGradient id="g" x1="0" y1="0" x2="0.4" y2="1">` +
    `<stop offset="0" stop-color="${p.from}"/><stop offset="1" stop-color="${p.to}"/>` +
    `</linearGradient></defs>` +
    `<rect width="1080" height="1920" fill="url(#g)"/>` +
    `<circle cx="900" cy="300" r="260" fill="#fff" opacity=".35"/>` +
    `<g fill="${p.ink}" font-family="system-ui,-apple-system,Segoe UI,Roboto,sans-serif">` +
    `<text y="760" font-size="92" font-weight="700">${head}</text>` +
    `<text y="${1180}" font-size="48" opacity=".85">${text}</text>` +
    `</g></svg>`
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

interface SeedText {
  title: string
  slide1: string
  slide2: string
  button: string
}

function story(
  id: number,
  palette: keyof typeof PALETTES,
  link: string,
  ru: SeedText,
  uz: SeedText,
): StoryDto {
  const first = slideSvg(palette, ru.slide1)
  const second = slideSvg(palette, ru.title, ru.slide2)
  return {
    id,
    title_ru: ru.title,
    title_uz: uz.title,
    cover_key: '',
    cover_url: '',
    slides: [
      {
        id: id * 10 + 1,
        image_key: first,
        image_url: first,
        duration_sec: 5,
        button_ru: '',
        button_uz: '',
        link: '',
      },
      {
        id: id * 10 + 2,
        image_key: second,
        image_url: second,
        duration_sec: 7,
        button_ru: ru.button,
        button_uz: uz.button,
        link,
      },
    ],
    starts_at: null,
    ends_at: null,
    sort: id,
    is_active: true,
    views: 0,
    created_at: '2026-10-01T09:00:00.000Z',
    updated_at: '2026-10-01T09:00:00.000Z',
  }
}

export const SEED_STORIES: StoryDto[] = [
  story(
    1,
    'mint',
    '/catalog',
    {
      title: 'Кэшбэк на витамины',
      slide1: 'Повышенный кэшбэк на витамины',
      slide2: 'Витамины и БАДы возвращают больше баллов, чем обычно. Баллы приходят после оплаты.',
      button: 'Выбрать витамины',
    },
    {
      title: 'Vitaminlarga keshbek',
      slide1: 'Vitaminlarga oshirilgan keshbek',
      slide2: 'Vitaminlar va BAD uchun odatdagidan ko‘proq ball qaytadi.',
      button: 'Vitamin tanlash',
    },
  ),
  story(
    2,
    'sky',
    '/categories',
    {
      title: 'Сезон простуды',
      slide1: 'Соберите аптечку к холодам',
      slide2: 'Жаропонижающее, средства от насморка и боли в горле, термометр.',
      button: 'Смотреть категории',
    },
    {
      title: 'Shamollash mavsumi',
      slide1: 'Sovuq kunlarga dori qutisini tayyorlang',
      slide2: 'Isitmani tushiruvchi, tumov va tomoq og‘rig‘iga qarshi vositalar.',
      button: 'Turkumlarni ko‘rish',
    },
  ),
  story(
    3,
    'sand',
    '/stores',
    {
      title: 'Самовывоз',
      slide1: 'Закажите онлайн — заберите в аптеке',
      slide2: 'Аптека соберёт заказ заранее. Придёт СМС, когда можно забирать.',
      button: 'Аптеки рядом',
    },
    {
      title: 'Olib ketish',
      slide1: 'Onlayn buyurtma bering — dorixonadan oling',
      slide2: 'Dorixona buyurtmani oldindan yig‘ib qo‘yadi.',
      button: 'Yaqin dorixonalar',
    },
  ),
  story(
    4,
    'rose',
    '/profile/edit',
    {
      title: 'День рождения',
      slide1: 'Баллы в подарок ко дню рождения',
      slide2: 'Укажите дату рождения в профиле — в этот день придут подарочные баллы.',
      button: 'Указать дату',
    },
    {
      title: 'Tug‘ilgan kun',
      slide1: 'Tug‘ilgan kuningizga sovg‘a ballar',
      slide2: 'Profilda tug‘ilgan sanangizni kiriting — o‘sha kuni sovg‘a ballar tushadi.',
      button: 'Sanani kiritish',
    },
  ),
]
